import { useState, useRef } from 'react'


import { zodResolver } from '@hookform/resolvers/zod'
import _ from 'lodash'
import { getSession, signIn } from '@/shared/services/session/compat'
import ReCAPTCHA from 'react-google-recaptcha'
import { FormProvider, useForm } from 'react-hook-form'




import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion'
import { env } from '@/env'
import { useAppRouter } from '@/shared/hooks/useAppRouter'
import { useTranslation } from '@/shared/hooks/useTranslation'
import { isViteRuntime } from '@/shared/lib/runtime/detect'
import { Button } from '@/shared/ui'
import { alert } from '@/shared/ui/dialog'
import { AppIcon  } from '@/shared/ui/icons'
import { Overlay } from '@/shared/ui/Overlay'

import { useAuthContext } from '../model/auth-context'
import { signInSchema } from '../model/schemas'
import { SignInFields } from './fields/SignInFields'
import { getSocialProviders, SocialProviders } from './providers/SocialProviders'

const isDev = env.NODE_ENV === 'development'

export const SignInForm = ({
  header: _header,
  onSignIn,
  onError,
  children,
  layoutVariant = 'default',
  showCredentials = true,
  onBack
}) => {
  const reCaptchaRef = useRef<ReCAPTCHA>(null)
  const {
    providers,
    styleConfig,
    reCaptchaEnabled,
    useReCaptchaV3,
    reCaptchaKey,
    goToSignUp,
    callbackUrl,
    setCurrentEmail,
    goToResendConfirmation,
    getDefaultValues,
    config
  } = useAuthContext()
  const isAuthPageLayout = layoutVariant === 'auth-page'
  const header = isAuthPageLayout
    ? null
    : _.merge(
        _.has(config, 'signIn.header') && _.get(config, 'signIn.header', {}),
        _header || {}
      )
  const hideSignUpAction =
    isAuthPageLayout ||
    (_.has(config, 'signIn.hideSignUpAction')
      ? _.get(config, 'signIn.hideSignUpAction')
      : !_.has(config, 'signUp'))
  const hasSocialProviders =
    Object.keys(getSocialProviders(providers)).length > 0
  const methods = useForm({
    defaultValues: getDefaultValues(),
    resolver: zodResolver(signInSchema)
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const router = useAppRouter()

  const { t } = useTranslation()

  const handleSignIn = async data => {
    if (isSubmitting) return

    setIsSubmitting(true)

    let navigated = false

    try {
      const { error, ok, status, url } = await signIn('domain-sign-in', {
        login: data.login,
        password: data.password,
        ...data,
        callbackUrl,
        redirect: false
      })

      if (ok && !error) {
        const session = await getSession()

        if (!session?.user) {
          methods.setError('password', {
            type: 'custom',
            message: t(
              'invalidCredentials',
              {},
              { fallback: 'Credenciais inválidas' }
            )
          })
          return
        }

        if (typeof onSignIn !== 'undefined' && _.isFunction(onSignIn)) {
          return onSignIn({
            error,
            ok,
            status,
            url,
            callbackUrl
          })
        }

        const target =
          typeof callbackUrl === 'string' && callbackUrl.length > 0
            ? callbackUrl
            : router.asPath

        if (isViteRuntime) {
          navigated = true
          await router.push(target)
          return
        }

        navigated = true
        window.location.replace(callbackUrl)
        return
      }

      if (error) {
      setCurrentEmail(data?.login)

      if (typeof onError !== 'undefined' && _.isFunction(onError))
        onError({ error, ok, status, url })
      else {
        if (error == 'invalid_password') {
          methods.setError('password', {
            type: 'custom',
            message: t(
              'auth.incorrectPassword',
              {},
              { fallback: 'Senha incorreta' }
            )
          } as never)
        } else if (error == 'login_not_found') {
          methods.setError('login', {
            type: 'custom',
            // O RHF tipa `message` como string, mas o `Field.Error` renderiza
            // ReactNode justamente para preservar mensagens com ação — aqui, o
            // "clique aqui" que leva ao cadastro.
            message: (
              <>
                {`${t(
                  'emailNotFound',
                  {},
                  { fallback: 'E-mail não encontrado' }
                )}, `}
                <span
                  onClick={() => goToSignUp()}
                  className="cursor-pointer text-primary"
                >
                  {t('buttons.clickHere', {}, { fallback: 'clique aqui' })}
                </span>{' '}
                {t(
                  'toCreateAnAccount',
                  {},
                  { fallback: 'para criar uma conta' }
                )}
              </>
            ) as unknown as string
          })
        } else if (error == 'unconfirmed') {
          methods.setError('password', {
            type: 'custom',
            message: (
              <>
                {`${t(
                  'accountNotConfirmed',
                  {},
                  { fallback: 'Conta não confirmada' }
                )}, `}
                <span
                  onClick={() => goToResendConfirmation()}
                  className="cursor-pointer text-primary"
                >
                  {t('buttons.clickHere', {}, { fallback: 'clique aqui' })}
                </span>{' '}
                {t(
                  'toRequestANewCode',
                  {},
                  { fallback: 'para solicitar um novo código' }
                )}
              </>
            ) as unknown as string
          })

          void alert({
            variant: 'destructive',
            title: t(
              'accountNotConfirmed',
              {},
              { fallback: 'Conta não confirmada' }
            ),
            description: t(
              'registeredButNotConfirmed',
              {},
              {
                fallback:
                  'Sua conta foi previamente registrada mas não foi confirmada, clique no botão abaixo para solicitar novo código de confirmação'
              }
            )
          }).then(() => goToResendConfirmation())
        } else
          methods.setError('password', {
            type: 'custom',
            message: t(
              'invalidCredentials',
              {},
              { fallback: 'Credenciais inválidas' }
            )
          })
      }
      }
    } catch {
      methods.setError('password', {
        type: 'custom',
        message: t(
          'invalidCredentials',
          {},
          { fallback: 'Credenciais inválidas' }
        )
      })
    } finally {
      if (!navigated) setIsSubmitting(false)
    }
  }

  const emailFields = (
    <>
      <SignInFields>{children}</SignInFields>

      {reCaptchaEnabled && (
        <div className="flex items-center justify-center w-full">
          <ReCAPTCHA
            ref={reCaptchaRef}
            sitekey={reCaptchaKey}
            size={useReCaptchaV3 ? 'invisible' : 'normal'}
          />
        </div>
      )}
      {/* Voltar e enviar dividem a linha: empilhados, custavam uma faixa
          inteira de altura numa tela em que o formulário já disputa a dobra
          com o cabeçalho. Os dois usam a mesma altura de controle, então a
          linha fecha sem ajuste. */}
      <div className="mt-8 flex items-center gap-2">
        {onBack ? (
          <Button
            type="button"
            variant="light"
            className="shrink-0"
            onClick={onBack}
            showTrailingIcon={false}
            leadingIcon={<AppIcon name="arrow-left" size={16} />}
            aria-label="Voltar para as opções de acesso"
          >
            Voltar
          </Button>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          className="grow"
          loading={isSubmitting}
          showTrailingIcon={false}
        >
          {isSubmitting
            ? t('buttons.signIng', {}, { fallback: 'Entrando...' })
            : t('buttons.signIn', {}, { fallback: 'Entrar' })}
        </Button>
      </div>
    </>
  )

  return (
    <Overlay
      block
      show={false}
      active={isSubmitting}
      opacity="50"
      layer={
        <AppIcon name="spinner-third" className="animate-spin text-brand text-2xl" />
      }
    >
      {header && (
        <div className="text-center mb-11">
          <h1 className="text-gray-900 font-extrabold mb-3">{header?.title}</h1>
          <div className="text-gray-700 font-semibold text-sm">
            {header?.subtitle}
          </div>
        </div>
      )}

      {!showCredentials && isAuthPageLayout ? null : (
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSignIn)}>
          <div className="flex flex-col gap-3">
            {!isAuthPageLayout && <SocialProviders />}
            {!isAuthPageLayout && hasSocialProviders ? (
              <Accordion type="single" collapsible className="border-0">
                <AccordionItem value="email" className="border-0">
                  <AccordionTrigger className="h-[var(--btn-h-md)] w-full justify-center gap-2 rounded-[var(--btn-r-md)] border-2 border-solid border-contour bg-white py-0 px-[var(--btn-px-md)] text-[length:var(--btn-fs-md)] font-[number:var(--btn-fw-md)] text-gray-700 hover:border-gray-300 hover:no-underline transition-colors">
                    <span className="flex items-center gap-2">
                      <AppIcon name="envelope" size={18} />
                      {t(
                        'auth.signInWithEmail',
                        {},
                        { fallback: 'Entrar com e-mail' }
                      )}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2">
                    {emailFields}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ) : (
              <div>{emailFields}</div>
            )}
          </div>

          {!hideSignUpAction && (
            <div className="text-gray-700 text-center font-semibold text-sm flex justify-center mt-3">
              {t(
                'auth.notAMemberYet',
                {},
                { fallback: 'Não faz parte ainda?' }
              )}
              <Button
                type="button"
                variant="link"
                className="ml-1 h-auto p-0 text-sm font-semibold"
                onClick={() => goToSignUp()}
              >
                {t('buttons.signUp', {}, { fallback: 'Cadastre-se' })}
              </Button>
            </div>
          )}
        </form>
      </FormProvider>
      )}
    </Overlay>
  )
}
